import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ThemeColors } from '../../../../Context/ThemeContext';
import { PeriodOption } from './PeriodDropdown';
import type { CustomDateRange } from '../types';

const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getComparisonRange = (startDate: string, endDate: string, override?: CustomDateRange['comparisonOverride']): { start: string; end: string } => {
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  const rangeDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  let compDays = rangeDays;
  if (override) {
    switch (override.unit) {
      case 'days': compDays = override.value; break;
      case 'weeks': compDays = override.value * 7; break;
      case 'months': compDays = override.value * 30; break;
    }
  }

  const compEnd = new Date(start);
  compEnd.setDate(compEnd.getDate() - 1);
  const compStart = new Date(compEnd);
  compStart.setDate(compStart.getDate() - compDays + 1);

  return {
    start: compStart.toISOString().split('T')[0],
    end: compEnd.toISOString().split('T')[0],
  };
};

interface UniversalFilterProps {
  onChangeAll: (value: PeriodOption) => void;
  onCustomRangeAll?: (range: CustomDateRange) => void;
  theme: ThemeColors;
}

export const UniversalFilter: React.FC<UniversalFilterProps> = ({ onChangeAll, onCustomRangeAll, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const today = useMemo(() => new Date().toISOString().split('T')[0], []);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [touched, setTouched] = useState(false);
  const [useOverride, setUseOverride] = useState(false);
  const [overrideValue, setOverrideValue] = useState(7);
  const [overrideUnit, setOverrideUnit] = useState<'days' | 'weeks' | 'months'>('days');

  const presetOptions: PeriodOption[] = ['1D', '7D', '30D', '90D'];

  const labels: Record<string, string> = {
    '1D': '1 Day (24h)',
    '7D': '7 Days (1 week)',
    '30D': '30 Days (1 month)',
    '90D': '90 Days (3 months)',
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowCustom(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const comparisonRange = useMemo(() => {
    if (!startDate || !endDate) return null;
    return getComparisonRange(startDate, endDate, useOverride ? { value: overrideValue, unit: overrideUnit } : undefined);
  }, [startDate, endDate, useOverride, overrideValue, overrideUnit]);

  const isValid = startDate && endDate && startDate <= endDate;

  const validationMessage = useMemo((): string | null => {
    if (!touched) return null;
    if (!startDate && !endDate) return null;
    if (!startDate) return 'Start date is required';
    if (!endDate) return 'End date is required';
    if (startDate > today) return 'Start date cannot be in the future';
    if (endDate > today) return 'End date cannot be in the future';
    if (startDate > endDate) return 'Start date cannot be after end date';
    return null;
  }, [touched, startDate, endDate, today]);

  const handleApplyCustom = () => {
    if (!isValid || !onCustomRangeAll) return;
    onCustomRangeAll({
      startDate,
      endDate,
      comparisonOverride: useOverride ? { value: overrideValue, unit: overrideUnit } : undefined,
    });
    setIsOpen(false);
    setShowCustom(false);
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', zIndex: 20 }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
          color: '#fff',
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '13px',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
        }}
      >
        <span style={{ fontSize: '14px' }}>⚡</span>
        Set All Filters
        <span
          style={{
            fontSize: '10px',
            transform: isOpen ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        >
          ▼
        </span>
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '40px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            overflow: 'hidden',
            minWidth: showCustom ? '260px' : '120px',
          }}
        >
          <div
            style={{
              padding: '8px 12px',
              fontSize: '11px',
              color: theme.textSecondary,
              borderBottom: `1px solid ${theme.border}`,
              fontWeight: 500,
            }}
          >
            Apply to all cards
          </div>

          {!showCustom ? (
            <>
              {presetOptions.map((option) => (
                <button
                  key={option}
                  onClick={() => {
                    onChangeAll(option);
                    setIsOpen(false);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '10px 12px',
                    background: 'transparent',
                    color: theme.text,
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                    textAlign: 'left',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#2563eb20')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  {labels[option]}
                </button>
              ))}

              {onCustomRangeAll && (
                <>
                  <div style={{ borderTop: `1px solid ${theme.border}` }} />
                  <button
                    onClick={() => setShowCustom(true)}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 12px',
                      background: 'transparent',
                      color: '#2563eb',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 600,
                      textAlign: 'left',
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#2563eb20')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    &#128197; Custom Range...
                  </button>
                </>
              )}
            </>
          ) : (
            <div style={{ padding: '12px' }}>
              <label style={{ fontSize: '11px', color: theme.textSecondary, display: 'block', marginBottom: '2px' }}>Start</label>
              <input
                type="date"
                value={startDate}
                max={endDate || today}
                onChange={(e) => { setStartDate(e.target.value); setTouched(true); }}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  borderRadius: '4px',
                  border: `1px solid ${theme.border}`,
                  background: theme.inputBg,
                  color: theme.text,
                  fontSize: '12px',
                  marginBottom: '8px',
                  boxSizing: 'border-box',
                }}
              />

              <label style={{ fontSize: '11px', color: theme.textSecondary, display: 'block', marginBottom: '2px' }}>End</label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                max={today}
                onChange={(e) => { setEndDate(e.target.value); setTouched(true); }}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  borderRadius: '4px',
                  border: `1px solid ${theme.border}`,
                  background: theme.inputBg,
                  color: theme.text,
                  fontSize: '12px',
                  marginBottom: '10px',
                  boxSizing: 'border-box',
                }}
              />

              {validationMessage && (
                <div style={{ fontSize: '11px', color: '#ef4444', marginBottom: '6px', marginTop: '-4px' }}>
                  {validationMessage}
                </div>
              )}

              {comparisonRange && (
                <div style={{ fontSize: '11px', color: theme.textSecondary, marginBottom: '8px', padding: '6px 8px', background: theme.inputBg, borderRadius: '4px' }}>
                  Comparing to: {formatDate(comparisonRange.start)} &ndash; {formatDate(comparisonRange.end)}
                </div>
              )}

              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: theme.textSecondary, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={useOverride}
                    onChange={(e) => setUseOverride(e.target.checked)}
                    style={{ margin: 0 }}
                  />
                  Custom comparison window
                </label>

                {useOverride && (
                  <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                    <input
                      type="number"
                      min={1}
                      max={365}
                      value={overrideValue}
                      onChange={(e) => setOverrideValue(Math.max(1, parseInt(e.target.value, 10) || 1))}
                      style={{
                        width: '60px',
                        padding: '4px 6px',
                        borderRadius: '4px',
                        border: `1px solid ${theme.border}`,
                        background: theme.inputBg,
                        color: theme.text,
                        fontSize: '12px',
                      }}
                    />
                    <select
                      value={overrideUnit}
                      onChange={(e) => setOverrideUnit(e.target.value as 'days' | 'weeks' | 'months')}
                      style={{
                        flex: 1,
                        padding: '4px 6px',
                        borderRadius: '4px',
                        border: `1px solid ${theme.border}`,
                        background: theme.inputBg,
                        color: theme.text,
                        fontSize: '12px',
                      }}
                    >
                      <option value="days">days</option>
                      <option value="weeks">weeks</option>
                      <option value="months">months</option>
                    </select>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={handleApplyCustom}
                  disabled={!isValid}
                  style={{
                    flex: 1,
                    padding: '6px 12px',
                    borderRadius: '4px',
                    border: 'none',
                    background: isValid ? '#2563eb' : '#4b5563',
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: isValid ? 'pointer' : 'default',
                    opacity: isValid ? 1 : 0.5,
                  }}
                >
                  Apply to All
                </button>
                <button
                  onClick={() => { setShowCustom(false); setTouched(false); }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '4px',
                    border: `1px solid ${theme.border}`,
                    background: 'transparent',
                    color: theme.text,
                    fontSize: '12px',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  Back
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
